import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import AdaptedText from "../components/AdaptedText";
import IntroSection from "../components/IntroSection";
import IntroSectionSkeleton from "../components/IntroSectionSkeleton";
import ModuleLockedModal from "../components/ModuleLockedModal";
import { api } from "../lib/api";
import { useIntro } from "../hooks/useIntro";
import { Search, Lock, ArrowRight, CheckCircle2, Circle, GraduationCap } from "lucide-react";

// Same shape as BLUEPRINT_INTRO in LaunchBlueprint.jsx — admins override via
// Admin → Intros (backend key: "course"). Fallback used when no override exists.
// Paste ANY of these into videoUrl and IntroSection auto-picks the renderer:
//   • YouTube share URL         "https://youtu.be/XXXXXXX"
//   • YouTube embed URL         "https://www.youtube.com/embed/XXXXXXX"
//   • Vimeo player URL          "https://player.vimeo.com/video/XXXXXXX"
//   • Direct MP4 / WEBM / MOV   "https://cdn.example.com/intro.mp4"
export const COURSE_INTRO = {
  eyebrow: "CPG Founder Program · Overview",
  title: "A course built by operators, not academics.",
  body: "Every module is a hands-on step in the CPG launch playbook. Watch the 3-minute overview, then work through the modules in the order that fits your stage — your progress and quizzes auto-save as you go.",
  videoUrl: "",
  bullets: [
    "17 core modules · quizzes, worksheets, and case studies included",
    "Certifications track your progress and unlock as you complete each module",
    "Return anytime — your seat is lifetime access on every paid plan",
  ],
};

export default function Course() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const { intro, ready: introReady } = useIntro("course", COURSE_INTRO);
  // Real progress from /api/progress/me — completed_slugs is the set of
  // modules the user finished ("Marked complete"), passed_slugs is the set
  // of modules whose 80% quiz they passed. Either counts as complete for
  // the module card status. Iter 51 fix: this used to be hardcoded by
  // index (`i === 0 ? "complete" : i === 1 ? "in-progress" : ...`) which
  // showed a fake 6% for every user regardless of actual progress.
  const [progress, setProgress] = useState(null);
  // Paid-user progression lock modal target. When set to a `locked_reason`
  // payload, `ModuleLockedModal` renders the "Complete Module X to unlock
  // Module Y" gate over the grid. Free users never enter this branch.
  const [lockedGate, setLockedGate] = useState(null);

  useEffect(() => {
    const loadModules  = () => api.get("/modules").then((r) => setItems(r.data)).catch(() => {});
    const loadProgress = () => api.get("/progress/me").then((r) => setProgress(r.data)).catch(() => setProgress({}));
    loadModules();
    loadProgress();
    // Live-refresh when another route completes a module (e.g. quiz
    // pass on /modules/{slug}) so the just-unlocked next module stops
    // showing the lock icon without a manual reload.
    const onProgress = () => { loadModules(); loadProgress(); };
    window.addEventListener("cpg:module-progress", onProgress);
    return () => window.removeEventListener("cpg:module-progress", onProgress);
  }, []);

  const passedSlugs    = new Set(progress?.passed_slugs || []);
  const completedSlugs = new Set(progress?.completed_slugs || []);
  // "Started" = watched the video OR marked the reading as done. The
  // backend doesn't expose this directly; derive from watched_slugs ∪
  // read_slugs which /progress/me does return.
  const startedSlugs = new Set([
    ...(progress?.watched_slugs || []),
    ...(progress?.read_slugs || []),
  ]);

  const statusFor = (m) => {
    if (m.locked) return "locked";
    if (passedSlugs.has(m.slug) || completedSlugs.has(m.slug)) return "complete";
    if (startedSlugs.has(m.slug)) return "in-progress";
    return "not-started";
  };

  const withStatus = items.map((it, i) => ({
    ...it,
    number: String(i + 1).padStart(2, "0"),
    status: statusFor(it),
  }));
  const filtered = withStatus.filter((m) => {
    if (tab === "in-progress" && m.status !== "in-progress") return false;
    if (tab === "completed" && m.status !== "complete") return false;
    if (!q.trim()) return true;
    return m.name.toLowerCase().includes(q.toLowerCase()) || (m.description || "").toLowerCase().includes(q.toLowerCase());
  });
  const done = withStatus.filter((x) => x.status === "complete").length;
  const total = withStatus.length || 1;
  const pct = Math.round((done / total) * 100);

  const tabs = [
    ["all", "All modules"], ["in-progress", "In progress"], ["completed", "Completed"],
  ];

  // Compute the Continue target from the FULL module list (never the
  // tab-filtered one) so switching to the "Completed" tab can't ever
  // change where Continue takes the user. Preference order:
  //   1) first in-progress unlocked module (they were mid-way through)
  //   2) first not-started unlocked module (the next thing to open)
  //   3) course is entirely finished → first complete module (harmless,
  //      the user has nowhere new to go)
  // Modules from /api/modules are already sorted by `order`, so the
  // first match in each category is the natural next step.
  const continueTarget =
    withStatus.find((m) => m.status === "in-progress" && !m.locked)
    ?? withStatus.find((m) => m.status === "not-started" && !m.locked)
    ?? withStatus.find((m) => m.status === "complete")
    ?? withStatus[0]
    ?? null;

  return (
    <AppShell title="Course">
      <div className="w-full">
        {/* Course overview intro — matches Launch Blueprint layout.
          * Skeleton avoids flashing hardcoded fallback copy on cold
          * visits (incognito / new device / cache-cleared). */}
        {introReady && intro ? (
          <IntroSection
            {...intro}
            progressKey="course_intro"
            ctaLabel="Jump to the modules"
            ctaHref="#course-modules"
            allowSeek
            configHint={<>Admin → Intros to edit copy + video. Fallback in <code>src/pages/Course.jsx</code>.</>}
            testId="course-intro"
          />
        ) : (
          <IntroSectionSkeleton testId="course-intro" />
        )}

        <div id="course-modules" className="mt-10">
          <PageHero
            eyebrow="Curriculum"
            icon={GraduationCap}
            title={<>The <span className="text-grad">CPG Founder Program</span></>}
            subtitle="Core modules, quizzes, worksheets, examples, and specialist certifications — everything you need to build a fundable brand."
          />
        </div>

        <div className="rounded-2xl p-6 bg-shell-side text-white flex items-center justify-between gap-6" data-testid="course-progress">
          <div className="flex-1">
            <p className="text-xs uppercase tracking-widest text-shell-accent font-semibold">Overall progress</p>
            <div className="mt-2 flex items-baseline gap-4">
              <p className="font-display text-5xl">{pct}%</p>
              <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full bg-shell-accent" style={{ width: `${pct}%` }} />
              </div>
            </div>
            <p className="mt-2 text-sm text-white/70">{done} of {total} modules complete</p>
          </div>
          <Link to={continueTarget ? `/modules/${continueTarget.slug}` : "/modules"}
            className="rounded-md bg-white text-shell-side px-4 py-2 text-sm font-semibold" data-testid="course-continue">
            Continue
          </Link>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="inline-flex rounded-full border border-shell-border p-1 bg-white">
            {tabs.map(([k, label]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`course-tab-${k}`}
                className={["px-4 py-2 rounded-full text-sm transition", tab === k ? "bg-shell-side text-white" : "text-foreground/70 hover:text-foreground"].join(" ")}>
                {label}
              </button>
            ))}
          </div>
          <div className="relative sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search modules" data-testid="course-search"
              className="w-full rounded-full border border-shell-border bg-white pl-9 pr-4 py-2 text-sm focus:outline-none focus:border-shell-accent" />
          </div>
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-4">
          {filtered.map((m) => {
            const cardCls = ["surface p-6 flex items-start gap-4 text-left",
              m.status === "in-progress" ? "!bg-shell-accent/15 !border-shell-accent/50" : "",
              m.locked ? "opacity-70" : "hover:-translate-y-0.5 transition-transform"].join(" ");
            const inner = (
              <>
                <p className="font-display text-3xl text-muted-foreground w-10 shrink-0">{m.number}</p>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold">{m.name}</p>
                  <AdaptedText
                    as="p" className="text-xs text-muted-foreground mt-1"
                    source="lms_module" sourceId={m.slug} field="description"
                    originalText={m.description}
                    showControls={false}
                  />
                  <StatusBadge status={m.status} lockedReason={m.locked_reason} />
                </div>
                {m.locked ? <Lock className="w-4 h-4 text-muted-foreground shrink-0" /> : <ArrowRight className="w-4 h-4 text-shell-side shrink-0" />}
              </>
            );
            // Paid-user sequential / bonus lock — open the modal so the
            // founder sees "Complete Module X to unlock Module Y" with a
            // Go-to CTA. Free-user tier locks AND Menu-Directory plan
            // locks route to /account/subscription so the upgrade path
            // is consistent with the /modules index cards.
            const reason = m.locked_reason;
            const paidLocked =
              reason?.kind === "sequential_core"
              || reason?.kind === "bonus_gated";
            if (paidLocked) {
              return (
                <button
                  key={m.slug}
                  type="button"
                  onClick={() => setLockedGate(reason)}
                  className={cardCls + " w-full"}
                  data-testid={`course-module-${m.slug}`}
                >
                  {inner}
                </button>
              );
            }
            const target = m.locked ? "/account/subscription" : `/modules/${m.slug}`;
            return (
              <Link key={m.slug} to={target} className={cardCls} data-testid={`course-module-${m.slug}`}>
                {inner}
              </Link>
            );
          })}
        </div>
      </div>
      <ModuleLockedModal gate={lockedGate} onClose={() => setLockedGate(null)} />
    </AppShell>
  );
}

function StatusBadge({ status, lockedReason }) {
  const map = {
    complete: { label: "COMPLETE", Icon: CheckCircle2, cls: "bg-green-100 text-green-700" },
    "in-progress": { label: "IN PROGRESS", Icon: Circle, cls: "bg-shell-accent/30 text-shell-side" },
    "not-started": { label: "NOT STARTED", Icon: Circle, cls: "bg-muted text-muted-foreground" },
    locked: { label: "LOCKED · UPGRADE TO UNLOCK", Icon: Lock, cls: "bg-muted text-muted-foreground" },
  };
  const { label, Icon, cls } = map[status] || map["not-started"];
  // For paid users the lock reason is progression, not tier — swap the
  // badge copy so the founder sees why the card is dark. Menu-Directory
  // plan gates (kind: "plan_gated") reuse the "UPGRADE" copy so the
  // /course badge matches the /modules card CTA — both route to
  // /account/subscription and both should read as upgrade prompts.
  let effectiveLabel = label;
  if (status === "locked" && lockedReason) {
    if (lockedReason.kind === "sequential_core") {
      effectiveLabel = `LOCKED · FINISH M${lockedReason.unlock_after_number}`;
    } else if (lockedReason.kind === "bonus_gated") {
      effectiveLabel = "LOCKED · BONUS";
    } else if (lockedReason.kind === "plan_gated") {
      effectiveLabel = "LOCKED · UPGRADE TO UNLOCK";
    }
  }
  return (
    <span className={["mt-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest rounded-md px-2 py-1", cls].join(" ")}>
      <Icon className="w-3 h-3" /> {effectiveLabel}
    </span>
  );
}
